import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { AdminRole, Affiliation } from '@/types/affiliation';
import { Admin } from '@/types/admin';
import { getFullName } from '@/utils/name';

import { Information } from './Information';
import { AssignRole } from './AssignRole';
import { DeleteMembership } from './DeleteMembership';
import { MembershipHint } from './MembershipHint';
import * as styles from './index.module.scss';

type Props = {
  children?: never;
  affiliations: Affiliation[];
  gloplaId: string;
  me: Admin;
  hasManagerInOrganization: boolean;
  isManager: boolean;
  onClickDelete: (adminId: number) => void;
  onClickUpdateRole: (adminId: number, role: AdminRole) => void;
};

export const MembershipList: React.FC<Props> = (props) => {
  const {
    affiliations,
    gloplaId,
    me,
    hasManagerInOrganization,
    isManager,
    onClickDelete,
    onClickUpdateRole,
  } = props;
  const { t } = useTranslation();

  return (
    <div className={styles.root}>
      <MembershipHint />

      <div className={styles.cardListWrapper}>
        {affiliations.map((affiliation) => {
          const { admin, role } = affiliation;
          const isMe = admin.id === me.id;

          return (
            <div
              data-test="admin"
              key={admin.id + affiliation.role}
              className={styles.card}
            >
              <div className={styles.cardInnerWrapper}>
                <div className={styles.leftSide}>
                  <div>
                    {!admin.activatedAt && (
                      <span>{t('text.membership.index.notActivated')}</span>
                    )}
                    <span className={styles.name}>
                      {getFullName(admin.firstName, admin.lastName)}
                    </span>
                    <span>
                      {getFullName(admin.kanaFirstName, admin.kanaLastName)}
                    </span>
                  </div>

                  <span data-test="admin-email">{admin.email}</span>
                </div>

                {isManager && (
                  <div className={styles.rightSide__manager}>
                    {isMe && (
                      <span className={styles.roleName}>
                        {t(`models.affiliation.roleType.${role}.name`)}
                      </span>
                    )}
                    {!isMe && (
                      <div className={classNames(styles.actions)}>
                        {!isMe && (
                          <>
                            <AssignRole
                              affiliation={affiliation}
                              onClickUpdateRole={onClickUpdateRole}
                            />

                            <DeleteMembership
                              {...{ affiliation, onClickDelete }}
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {!isManager && (
                  <div className={styles.rightSide__nonmanager}>
                    <span>{t(`models.affiliation.roleType.${role}.name`)}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!hasManagerInOrganization && <Information gloplaId={gloplaId} />}
    </div>
  );
};
